import { Component, Input, OnInit, OnChanges, SimpleChanges, ChangeDetectionStrategy, ChangeDetectorRef, Output, EventEmitter, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { BarLineComboChartComponent } from '../../charts/bar-line-combo-chart/bar-line-combo-chart.component';
import { CompositeScoreWidgetComponent } from '../composite-score-widget/composite-score-widget.component';
import { CommonPerformanceTableComponent } from '../common-performance-table/common-performance-table.component';
import { BlockUIService } from '../../../services/block-ui.service';
import { TemplateCommonService } from '../template-ai-common/services/template-common.service';
import { GenericTemplateCommonService } from '../template-ai-common/services/generic-template-common.service';
import { BrandIndexService } from '../../../services/brand-index.service';
import { sampleAICitationsData } from '../../sample-education/ai-citations';

import { AICitationsResponse } from '../../../services/brand-index.service';
import { CommonSourcesComponent } from '../template-ai-common/common-sources/common-sources.component';
import { CommonStrengthsImprovementComponent, StrengthsImprovementData, BrandAnalysis } from '../common-strengths-improvement/common-strengths-improvement.component';

@Component({
  selector: 'app-ai-citations',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatExpansionModule,
    MatChipsModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    NgxChartsModule,
    CommonSourcesComponent,
    BarLineComboChartComponent,
    CompositeScoreWidgetComponent,
    CommonPerformanceTableComponent,
    CommonStrengthsImprovementComponent
    // CitationBadgeComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './ai-citations.component.html',
  styleUrl: './ai-citations.component.scss'


})
export class AICitationsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() title: string = 'AI Citations';

  // Core data properties
  aiCitationsResponse: AICitationsResponse | null = null;
  universities: any[] = [];
  context: any = null;
  metadata: any = null;
  evidenceSources: any[] = [];
  recommendations: any[] = [];

  // Self-contained data loading properties
  isLoading: boolean = false;
  error: string | null = null;
  hasLoadedData: boolean = false;
  private dataCache: any = null;

  // Table properties
  tableColumns: any[] = [];
  tableRows: any[] = [];

  loadingSub: any;

  // Chart data properties
  barChartData: any[] = [];
  lineChartData: any[] = [];

  // Combo Chart V2 properties
  results: any[] = [];
  lineChart: any[] = [];
  colors: any;
  transform: string = '';
  dims: any;
  xScale: any;
  yScale: any;

  // Composite Score Card Data - Dynamic from API
  compositeScore: number = 0;
  scoreLevel: string = 'Low';
  compositeScoreData: any[] = [];
  pieColorScheme: any = {
    domain: ['#ff9800', '#e0e0e0']
  };
  metricsData: any[] = [];

  // Color schemes for charts - different colors for each bar
  colorScheme: any = {
    domain: ['#1976d2', '#ff9800', '#4caf50', '#f44336', '#9c27b0', '#00bcd4', '#795548', '#607d8b']
  };

  // Responsive view dimensions
  view: [number, number] = [600, 400];

  // Combo Chart Color Schemes - matching ngx-charts structure
  comboBarScheme: any = {
    name: 'singleLightBlue',
    selectable: true,
    group: 'ordinal',
    domain: ['#1976d2']
  };

  lineChartScheme: any = {
    name: 'coolthree',
    selectable: true,
    group: 'ordinal',
    domain: ['#01579b', '#7aa3e5', '#a8385d', '#00bfa5']
  };

  get firstUniversityName(): string {
    return this.getFirstUniversityName();
  }

  // Combo Chart Axis Scaling and Formatting - matching ngx-charts structure
  yLeftAxisScale(min: number, max: number): any {
    return { min: `${min}`, max: `${max}` };
  }

  yRightAxisScale(min: number, max: number): any {
    return { min: `${min}`, max: `${max}` };
  }

  yLeftTickFormat(data: number): string {
    return `${data.toLocaleString()}`;
  }

  yRightTickFormat(data: number): string {
    return `${data}%`;
  }

  // Chart event handlers
  onSelect(event: any): void {
    // console.log('AI Citations chart item selected:', event);
  }

  onActivate(event: any): void {
    // console.log('AI Citations combo chart activated:', event);
  }

  onDeactivate(event: any): void {
    // console.log('AI Citations combo chart deactivated:', event);
  }

  // Cached processed data to avoid recalculation
  private _cachedBarData: any[] = [];
  private _cachedLineData: any[] = [];
  private _cachedFirstUniversityName: string = '';

  // Combined chart data for proper ngx-charts combo chart
  private _combinedChartData: any[] = [];
  private _combinedLineData: any[] = [];

  // Unified chart data - single array with both bar and line series
  private _unifiedChartData: any[] = [];

  // Getter properties for template optimization
  get processedBarData(): any[] {
    return this.getProcessedBarData();
  }

  get processedLineData(): any[] {
    return this.getProcessedLineData();
  }

  // Combined chart data getters for proper ngx-charts combo chart
  get combinedChartData(): any[] {
    return this._combinedChartData;
  }

  get combinedLineData(): any[] {
    return this._combinedLineData;
  }

  // Unified chart data getter
  get unifiedChartData(): any[] {
    return this._unifiedChartData;
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private blockUIService: BlockUIService,
    private templateCommonService: TemplateCommonService,
    private genericTemplateCommonService: GenericTemplateCommonService,
    private brandIndexService: BrandIndexService
  ) {}


  // Chart dimension and scale methods
  updateXAxisHeight(height: number): void {
    // Update x-axis height
  }

  updateYAxisWidth(width: number): void {
    // Update y-axis width
  }

  // Axis tick formatting methods
  xAxisTickFormatting(value: any): string {
    return value;
  }

  yAxisTickFormatting(value: any): string {
    return value.toString();
  }

  // Legend position for combo chart
  legendPosition: any = 'right';

  // Combo chart properties
  width = 600;
  height = 400;
  legendSpacing = 150;
  legend = true;
  legendOptions: any = {};
  activeEntries: any[] = [];
  animations = true;
  xAxis = true;
  yAxis = true;
  showXAxisLabel = true;
  showYAxisLabel = true;
  xAxisLabel = 'AI Citations Metrics';
  yAxisLabel = 'Score';
  showGridLines = true;
  trimXAxisTicks = true;
  trimYAxisTicks = true;
  rotateXAxisTicks = true;
  maxXAxisTickLength = 16;
  maxYAxisTickLength = 16;
  roundEdges = true;
  gradient = false;
  tooltipDisabled = false;


  ngOnInit() {
    // console.log('AI Citations - ngOnInit() called');

    this.loadingSub = this.blockUIService.isLoading$.subscribe(status => {
      if (status === true) {
        // Clear data when loading starts
        this.universities = [];
        this.aiCitationsResponse = null;
      }
    });

    // Initialize table columns
    this.initializeTableColumns();

    // Load default data when component initializes
    this.loadDefaultData();

    this.updateViewByBreakpoint();
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.updateViewByBreakpoint, { passive: true });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // Handle any input changes if needed
  }

  ngOnDestroy() {
    if (this.loadingSub) {
      this.loadingSub.unsubscribe();
    }
  }

  // Public method to refresh data when API call completes
  public refreshDataWithNewData(newData: AICitationsResponse) {
    // console.log('🔄 AI Citations: Refreshing with new data:', newData);
    // console.log('📋 AI Citations: Complete API response structure:', JSON.stringify(newData, null, 2));

    if (newData && newData.universities) {
      // console.log('🏫 AI Citations: Universities data:', newData.universities);
      // console.log('🔢 AI Citations: Total universities count:', newData.universities.length);
      newData.universities.forEach((uni: any, index: number) => {
        // console.log(`🏫 AI Citations: University ${index + 1} data:`, uni);
        // console.log(`🔑 AI Citations: University ${index + 1} available properties:`, Object.keys(uni));
      });
    } else {
      // console.log('❌ AI Citations: Invalid or empty data received');
    }

    this.aiCitationsResponse = newData;
    this.clearCache();
    this.updateChartData();
    this.updateCompositeScoreData();

    // Debug current state
    this.debugCurrentState();

    this.cdr.markForCheck();
  }

  // Public method to check if component has data
  public hasData(): boolean {
    return !!(this.aiCitationsResponse && this.universities && this.universities.length > 0);
  }

  // Initialize table columns
  private initializeTableColumns(): void {
    this.tableColumns = [
      { key: 'name', header: 'University', className: 'university-column' },
      { key: 'citation_frequency', header: 'Citation Frequency', className: 'metric-column' },
      { key: 'citation_share_of_voice', header: 'Citation Share of Voice', className: 'metric-column' },
      { key: 'authority_weight', header: 'Authority Weight', className: 'metric-column' },
      { key: 'relevance_score', header: 'Relevance Score', className: 'metric-column' },
      { key: 'ai_readability', header: 'AI Readability', className: 'metric-column' },
      { key: 'external_mentions', header: 'External Mentions', className: 'metric-column' },
      { key: 'diversity_index', header: 'Diversity Index', className: 'metric-column' },
      { key: 'persistence_score', header: 'Persistence Score', className: 'metric-column' }
    ];
  }

  // Self-contained data loading methods
  private loadDefaultData(): void {
    // console.log('AI Citations - loadDefaultData() called');
    // Load sample data by default
    this.loadSampleData();
  }

  private loadSampleData(): void {
    // console.log('AI Citations - loadSampleData() called');
    // Load sample AI citations data from the imported sample file
    this.processAICitationsData(sampleAICitationsData);
    this.hasLoadedData = true;
  }

  public loadApiData(forceRefresh: boolean = false): void {
    // Try to get filter data from both services
    let currentFilterData = this.templateCommonService.getCurrentFilterData();
    let compareClicked = this.templateCommonService.getCompareClicked();

    // If no data from regular service, try generic service
    if (!currentFilterData) {
      const genericFilterData = this.genericTemplateCommonService.getCurrentFilterData();
      if (genericFilterData) {
        // Convert generic filter data to regular format
        currentFilterData = {
          selectedEntities: genericFilterData.selectedEntities || [],
          category: genericFilterData.category || 'MBA',
          country: genericFilterData.country || 'Global',
          models: genericFilterData.models || ['gpt-4', 'claude-3'],
          experience_level: genericFilterData.experience_level || 'intermediate',
          budget_range: genericFilterData.budget_range || 'premium'
        };
      }
      compareClicked = this.genericTemplateCommonService.getCompareClicked();
    }

    // console.log("AI Citations - currentFilterData:", currentFilterData);
    const hasFilterData = currentFilterData &&
      currentFilterData.selectedEntities &&
      currentFilterData.selectedEntities.length > 0 &&
      currentFilterData.category &&
      currentFilterData.country;

    // console.log("AI Citations - hasFilterData:", hasFilterData, "forceRefresh:", forceRefresh);

    if (!hasFilterData) {
      // console.log('AI Citations - No filter data available, using sample data');
      this.loadSampleData();
      return;
    }

    if (!forceRefresh && this.dataCache) {
      // console.log('AI Citations - Using cached data');
      this.processAICitationsData(this.dataCache);
      return;
    }

    // console.log('AI Citations - Loading API data');
    this.isLoading = true;
    this.error = null;

    // Convert universities to string array
    const universities = currentFilterData!.selectedEntities.map(entity => entity.name);

    const payload = {
      universities: universities,
      course: currentFilterData!.category,
      country: currentFilterData!.country,
      include_competitors: true,
      force_refresh: forceRefresh
    };

    // console.log('AI Citations - API payload:', payload);

    this.brandIndexService.analyzeAICitations(payload).subscribe({
      next: (response: AICitationsResponse) => {
        // console.log('AI Citations - API data loaded successfully:', response);
        this.dataCache = response;
        this.processAICitationsData(response);
        this.isLoading = false;
        this.hasLoadedData = true;
      },
      error: (error: any) => {
        console.error('AI Citations - API error:', error);

        // Extract detailed error message
        let errorMessage = 'Failed to load AI citations data';
        if (error?.error?.message) {
          errorMessage = error.error.message;
        } else if (error?.message) {
          errorMessage = error.message;
        } else if (typeof error === 'string') {
          errorMessage = error;
        }

        this.error = errorMessage;
        this.isLoading = false;
        // Fallback to sample data on error
        // console.log('AI Citations - Falling back to sample data due to error');
        this.loadSampleData();
      }
    });
  }

  public refreshData(): void {
    // console.log('AI Citations - refreshData() called');
    this.loadApiData(true);
  }

  private processAICitationsData(data: any): void {
    // Store the raw data for reference
    this.aiCitationsResponse = data;

    // console.log('AI Citations: Processing data:', data);

    // Extract all the rich data from the response
    this.universities = data.universities || [];
    this.context = data.context || null;
    this.metadata = data.metadata || null;
    this.evidenceSources = data.context?.citations || [];
    this.recommendations = data.context?.recommendations || [];

    // console.log('AI Citations: Extracted evidenceSources:', this.evidenceSources);
    // console.log('AI Citations: Extracted recommendations:', this.recommendations);

    // Process chart data
    this.updateChartData();

    // Process composite score data
    this.updateCompositeScoreData();

    // Process table data
    this.processTableData();

    this.cdr.markForCheck();
  }

  private processTableData(): void {
    // Use universities data for table
    if (this.universities && this.universities.length > 0) {
      this.tableRows = this.universities.map(university => ({
        name: university.name,
        citation_frequency: university.citation_frequency || 0,
        citation_share_of_voice: university.citation_share_of_voice || 0,
        authority_weight: university.authority_weight || 0,
        relevance_score: university.relevance_score || 0,
        ai_readability: university.ai_readability || 0,
        external_mentions: university.external_mentions || 0,
        diversity_index: university.diversity_index || 0,
        persistence_score: university.persistence_score || 0,
        is_your_brand: this.isYouUniversity(university)
      }));
    } else {
      this.tableRows = [];
    }
  }


  // Most cited domains: from response (context.citations_table or structured_data.context.citations_table) or fallback sample
  get citedDomainsData(): any[] {
    const table = this.aiCitationsResponse?.context?.citations_table
      ?? this.aiCitationsResponse?.structured_data?.context?.citations_table;
    if (Array.isArray(table) && table.length > 0) {
      return table.map((row: any) => ({
        domain: row.domain ?? '',
        authority_score: row.authority_score ?? 0,
        category: row.category,
        monthly_traffic: row.monthly_traffic ?? ''
      }));
    }
    return [
      { domain: 'scholar.google.com', authority_score: 95, monthly_traffic: '2.5B' },
      { domain: 'researchgate.net', authority_score: 88, monthly_traffic: '180M' },
      { domain: 'academia.edu', authority_score: 82, monthly_traffic: '120M' },
      { domain: 'arxiv.org', authority_score: 90, monthly_traffic: '45M' },
      { domain: 'pubmed.ncbi.nlm.nih.gov', authority_score: 92, monthly_traffic: '85M' },
      { domain: 'ieee.org', authority_score: 87, monthly_traffic: '65M' },
      { domain: 'springer.com', authority_score: 85, monthly_traffic: '55M' },
      { domain: 'wiley.com', authority_score: 83, monthly_traffic: '48M' }
    ];
  }

  // Public method to get current data status
  public getDataStatus(): string {
    if (!this.aiCitationsResponse) return 'No data';
    if (!this.universities) return 'No universities data';
    if (this.universities.length === 0) return 'Empty universities array';
    return `Ready with ${this.universities.length} universities`;
  }

  // Debug method to check current component state
  private debugCurrentState(): void {
    // console.log('🔍 AI Citations: CURRENT STATE DEBUG');
    // console.log('📊 shouldShowBarChart():', this.shouldShowBarChart());
    // console.log('📈 shouldShowComboChart():', this.shouldShowComboChart());
    // console.log('📋 barChartData length:', this.barChartData.length);
    // console.log('📈 lineChartData length:', this.lineChartData.length);
    // console.log('🏫 Universities count:', this.universities?.length || 0);
  }

  // Determine if we should show bar chart only (single university)
  shouldShowBarChart(): boolean {
    // Always return false - we'll use combo-chart-v2 for both single and multiple universities
    // console.log('AI Citations: shouldShowBarChart() - Always returning false, using combo-chart-v2 for all cases');
    return false;
  }

  // Check if we should show only the "you" university (for single analysis mode)
  private shouldShowOnlyYouUniversity(): boolean {
    // Return false to show combo chart for multiple universities
    // Return true only for true single university analysis
    return false;
  }

  // Determine if we should show combo chart (now used for both single and multiple universities)
  shouldShowComboChart(): boolean {
    if (!this.universities) {
      // console.log('AI Citations: shouldShowComboChart() - No universities data, returning false');
      return false;
    }

    const universities = this.universities;
    const shouldShow = universities.length > 0; // Show combo chart for any number of universities
    // console.log('🎯 AI Citations: shouldShowComboChart() - Universities count:', universities.length, 'Should show combo chart:', shouldShow);

    return shouldShow;
  }

  // Get appropriate chart subtitle
  getChartSubtitle(): string {
    if (!this.universities) return 'AI Citations scores comparison';

    const universities = this.universities;
    if (universities.length === 1) {
      const university = universities[0];
      return `📊 Single University Analysis: ${university.name} - Detailed AI Citations metrics breakdown`;
    } else {
      const youUniversity = this.findYouUniversity(universities);
      const competitorCount = universities.length - 1;
      if (youUniversity) {
        return `AI Citations comparison: ${youUniversity.name} (bars) vs ${competitorCount} competitor${competitorCount > 1 ? 's' : ''} (lines)`;
      } else {
        return `AI Citations comparison across ${universities.length} universities`;
      }
    }
  }

  // Update responsive view dimensions
  private updateViewByBreakpoint = () => {
    const width = typeof window !== 'undefined' ? window.innerWidth : 1024;
    if (width <= 480) {
      this.view = [300, 300];
    } else if (width <= 768) {
      this.view = [400, 350];
    } else if (width <= 1024) {
      this.view = [500, 400];
    } else {
      this.view = [600, 400];
    }
    this.cdr.markForCheck();
  }

  // Update chart data based on current response
  private updateChartData() {
    // console.log('🔄 AI Citations: updateChartData() called');
    // console.log('🔄 AI Citations: aiCitationsResponse:', this.aiCitationsResponse);

    if (!this.aiCitationsResponse) {
      // console.log('❌ AI Citations: No aiCitationsResponse available');
      this.barChartData = [];
      this.lineChartData = [];
      return;
    }

    if (!this.universities) {
      // console.log('❌ AI Citations: No universities data available');
      this.barChartData = [];
      this.lineChartData = [];
      return;
    }

    const universities = this.universities;
    // console.log('✅ AI Citations: Processing universities:', universities);
    // console.log('✅ AI Citations: Universities count:', universities.length);

    if (this.shouldShowBarChart()) {
      // Single university - show bar chart with detailed metrics
      // console.log('🎯 AI Citations: SINGLE UNIVERSITY ANALYSIS MODE');
      // console.log('📊 AI Citations: Showing bar chart only for single university:', universities[0].name);
      // console.log('📈 AI Citations: Universities count:', universities.length);
      // console.log('🔍 AI Citations: University data:', universities[0]);

      this.barChartData = this.prepareBarChartData(universities[0]);
      this.lineChartData = []; // FORCE clear line data for single university
      // console.log('✅ AI Citations: Bar chart data prepared:', this.barChartData);
      // console.log('🚫 AI Citations: Line chart data cleared for single university:', this.lineChartData);
    } else if (this.shouldShowComboChart()) {
      // Multiple universities - show combo chart (bars for "you" + lines for competitors)
      const youUniversity = this.findYouUniversity(universities);
      const competitorUniversities = this.findCompetitorUniversities(universities);

      // console.log('🎯 AI Citations: Showing UNIFIED combo chart');
      // console.log('📊 AI Citations: You university:', youUniversity?.name);
      // console.log('📈 AI Citations: Competitor universities:', competitorUniversities.map(u => u.name));

      // Use the new unified chart data preparation method
      this.prepareUnifiedChartData(youUniversity, competitorUniversities);

      // Keep legacy data for backward compatibility
      this.prepareCombinedChartData(youUniversity, competitorUniversities);

      // Prepare combo chart data
      this.prepareComboChartData(youUniversity, competitorUniversities);

      if (youUniversity) {
        this.barChartData = this.prepareBarChartData(youUniversity);
      } else {
        this.barChartData = [];
      }

      if (competitorUniversities.length > 0) {
        this.lineChartData = this.prepareLineChartData(competitorUniversities);
      } else {
        this.lineChartData = [];
      }
    }

    // console.log('AI Citations: Final bar chart data:', this.barChartData);
    // console.log('AI Citations: Final line chart data:', this.lineChartData);

    // Prepare combo-chart-v2 data
    this.prepareComboChartV2Data();
  }

  // Prepare data for combo-chart-v2 component
  private prepareComboChartV2Data() {
    if (!this.universities || this.universities.length === 0) {
      this.results = [];
      this.lineChart = [];
      return;
    }

    const universities = this.universities;
    const youUniversity = this.findYouUniversity(universities);
    const competitorUniversities = this.findCompetitorUniversities(universities);

    // Prepare bar chart data (results) - YOU university metrics as bars
    if (youUniversity) {
      this.results = [
        { name: 'Citation Frequency', value: youUniversity.citation_frequency || 0 },
        { name: 'Citation Share of Voice', value: youUniversity.citation_share_of_voice || 0 },
        { name: 'Authority Weight', value: youUniversity.authority_weight || 0 },
        { name: 'Relevance Score', value: youUniversity.relevance_score || 0 },
        { name: 'AI Readability', value: youUniversity.ai_readability || 0 },
        { name: 'External Mentions', value: youUniversity.external_mentions || 0 },
        { name: 'Diversity Index', value: youUniversity.diversity_index || 0 },
        { name: 'Persistence Score', value: youUniversity.persistence_score || 0 }
      ];
    } else {
      this.results = [];
    }

    // Prepare line chart data (lineChart) - competitor universities as lines
    if (competitorUniversities.length > 0) {
      this.lineChart = competitorUniversities.map(university => ({
        name: university.name,
        series: [
          { name: 'Citation Frequency', value: university.citation_frequency || 0 },
          { name: 'Citation Share of Voice', value: university.citation_share_of_voice || 0 },
          { name: 'Authority Weight', value: university.authority_weight || 0 },
          { name: 'Relevance Score', value: university.relevance_score || 0 },
          { name: 'AI Readability', value: university.ai_readability || 0 },
          { name: 'External Mentions', value: university.external_mentions || 0 },
          { name: 'Diversity Index', value: university.diversity_index || 0 },
          { name: 'Persistence Score', value: university.persistence_score || 0 }
        ]
      }));
    } else {
      this.lineChart = [];
    }

    // Set colors
    this.colors = this.comboBarScheme;

    // console.log('AI Citations: Combo-chart-v2 results prepared:', this.results);
    // console.log('AI Citations: Combo-chart-v2 lineChart prepared:', this.lineChart);
  }


  // Update composite score data dynamically from API response
  private updateCompositeScoreData() {
    if (!this.universities || this.universities.length === 0) {
      this.compositeScore = 0;
      this.scoreLevel = 'Low';
      this.compositeScoreData = [];
      this.metricsData = [];
      return;
    }

    // For single university, use that university's data
    // For multiple universities, use the "you" university or first university
    let targetUniversity = this.universities[0];

    if (this.universities.length > 1) {
      const youUniversity = this.findYouUniversity(this.universities);
      if (youUniversity) {
        targetUniversity = youUniversity;
      }
    }

    // Calculate composite score as average of all citation metrics
    const metrics = [
      targetUniversity.citation_frequency || 0,
      targetUniversity.citation_share_of_voice || 0,
      targetUniversity.authority_weight || 0,
      targetUniversity.relevance_score || 0,
      targetUniversity.ai_readability || 0,
      targetUniversity.external_mentions || 0,
      targetUniversity.diversity_index || 0,
      targetUniversity.persistence_score || 0
    ];

    this.compositeScore = Math.round(metrics.reduce((sum, score) => sum + score, 0) / metrics.length);

    // Determine score level
    if (this.compositeScore >= 80) {
      this.scoreLevel = 'High';
    } else if (this.compositeScore >= 60) {
      this.scoreLevel = 'Medium';
    } else {
      this.scoreLevel = 'Low';
    }

    // Update composite score data for pie chart
    this.compositeScoreData = [
      { name: 'Score', value: this.compositeScore },
      { name: 'Remaining', value: 100 - this.compositeScore }
    ];

    // Update metrics data for display
    this.metricsData = [
      { label: 'Citation Frequency', value: targetUniversity.citation_frequency || 0 },
      { label: 'Citation Share of Voice', value: targetUniversity.citation_share_of_voice || 0 },
      { label: 'Authority Weight', value: targetUniversity.authority_weight || 0 },
      { label: 'Relevance Score', value: targetUniversity.relevance_score || 0 },
      { label: 'AI Readability', value: targetUniversity.ai_readability || 0 },
      { label: 'External Mentions', value: targetUniversity.external_mentions || 0 },
      { label: 'Diversity Index', value: targetUniversity.diversity_index || 0 },
      { label: 'Persistence Score', value: targetUniversity.persistence_score || 0 }
    ];

    // console.log('🔄 AI Citations: Updated composite score data:', {
    //   compositeScore: this.compositeScore,
    //   scoreLevel: this.scoreLevel,
    //   compositeScoreData: this.compositeScoreData,
    //   metricsData: this.metricsData,
    //   targetUniversity: targetUniversity
    // });
  }

  // Prepare bar chart data for single university - using dynamic data from API
  private prepareBarChartData(university: any): any[] {
    // console.log('🎯 AI Citations: Preparing SINGLE UNIVERSITY bar chart data');
    // console.log('📋 AI Citations: University object:', university);
    // console.log('🔑 AI Citations: Available properties:', Object.keys(university));

    // Use the actual data structure from the API response
    const chartData = [];

    // Add all available citation metrics dynamically
    if (university.citation_frequency !== undefined) {
      chartData.push({ name: 'Citation Frequency', value: university.citation_frequency });
    }
    if (university.citation_share_of_voice !== undefined) {
      chartData.push({ name: 'Citation Share of Voice', value: university.citation_share_of_voice });
    }
    if (university.authority_weight !== undefined) {
      chartData.push({ name: 'Authority Weight', value: university.authority_weight });
    }
    if (university.relevance_score !== undefined) {
      chartData.push({ name: 'Relevance Score', value: university.relevance_score });
    }
    if (university.ai_readability !== undefined) {
      chartData.push({ name: 'AI Readability', value: university.ai_readability });
    }
    if (university.external_mentions !== undefined) {
      chartData.push({ name: 'External Mentions', value: university.external_mentions });
    }
    if (university.diversity_index !== undefined) {
      chartData.push({ name: 'Diversity Index', value: university.diversity_index });
    }
    if (university.persistence_score !== undefined) {
      chartData.push({ name: 'Persistence Score', value: university.persistence_score });
    }

    // Add any other metrics that might be in the API response
    Object.keys(university).forEach(key => {
      if (typeof university[key] === 'number' &&
          !['citation_frequency', 'citation_share_of_voice', 'authority_weight', 'relevance_score', 'ai_readability', 'external_mentions', 'diversity_index', 'persistence_score'].includes(key)) {
        const metricName = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        chartData.push({ name: metricName, value: university[key] });
      }
    });

    // console.log('AI Citations: Generated bar chart data:', chartData);
    return chartData;
  }

  // Prepare line chart data for multiple universities - using dynamic data from API
  private prepareLineChartData(universities: any[]): any[] {
    // console.log('AI Citations: Preparing line chart data for universities:', universities);

    // For combo chart, line chart should show each competitor university as a separate line
    // Each line represents all metrics for that university
    return universities.map(university => ({
      name: university.name,
      series: [
        { name: 'Citation Frequency', value: university.citation_frequency || 0 },
        { name: 'Citation Share of Voice', value: university.citation_share_of_voice || 0 },
        { name: 'Authority Weight', value: university.authority_weight || 0 },
        { name: 'Relevance Score', value: university.relevance_score || 0 },
        { name: 'AI Readability', value: university.ai_readability || 0 },
        { name: 'External Mentions', value: university.external_mentions || 0 },
        { name: 'Diversity Index', value: university.diversity_index || 0 },
        { name: 'Persistence Score', value: university.persistence_score || 0 }
      ]
    }));
  }

  // Prepare combined chart data for proper ngx-charts combo chart structure
  private prepareCombinedChartData(youUniversity: any, competitorUniversities: any[]): void {
    // console.log('🎯 AI Citations: Preparing combined chart data for ngx-charts combo chart');
    // console.log('📊 AI Citations: You university:', youUniversity?.name);
    // console.log('📈 AI Citations: Competitor universities:', competitorUniversities.map(u => u.name));

    // Prepare bar chart data (for "YOU" university)
    if (youUniversity) {
      this._combinedChartData = this.prepareBarChartData(youUniversity);
    } else {
      this._combinedChartData = [];
    }

    // Prepare line chart data (for competitor universities) - each competitor as a separate line
    if (competitorUniversities.length > 0) {
      this._combinedLineData = competitorUniversities.map(university => ({
        name: university.name,
        series: [
          { name: 'AI Citations Score', value: university.ai_visibility_score || 0 },
          { name: 'Content Quality', value: university.content_quality || 0 },
          { name: 'Digital Presence', value: university.digital_presence || 0 },
          { name: 'Innovation Index', value: university.innovation_index || 0 },
          { name: 'AI Adoption Rate', value: university.ai_adoption_rate || 0 }
        ]
      }));
    } else {
      this._combinedLineData = [];
    }

    // console.log('✅ AI Citations: Combined chart data prepared:', {
    //   barData: this._combinedChartData,
    //   lineData: this._combinedLineData
    // });
  }

  // Prepare unified chart data - single array with both bar and line series
  private prepareUnifiedChartData(youUniversity: any, competitorUniversities: any[]): void {
    // console.log('🎯 AI Citations: Preparing unified chart data');
    // console.log('📊 AI Citations: You university:', youUniversity?.name);
    // console.log('📈 AI Citations: Competitor universities:', competitorUniversities.map(u => u.name));

    // Create a single array that combines both bar and line data
    const unifiedData: any[] = [];

    // Add "YOU" university as bar series - each metric as a separate bar
    if (youUniversity) {
      const metrics = [
        { name: 'Citation Frequency', value: youUniversity.citation_frequency || 0 },
        { name: 'Citation Share of Voice', value: youUniversity.citation_share_of_voice || 0 },
        { name: 'Authority Weight', value: youUniversity.authority_weight || 0 },
        { name: 'Relevance Score', value: youUniversity.relevance_score || 0 },
        { name: 'AI Readability', value: youUniversity.ai_readability || 0 },
        { name: 'External Mentions', value: youUniversity.external_mentions || 0 },
        { name: 'Diversity Index', value: youUniversity.diversity_index || 0 },
        { name: 'Persistence Score', value: youUniversity.persistence_score || 0 }
      ];

      metrics.forEach(metric => {
        unifiedData.push({
          name: metric.name,
          value: metric.value,
          series: youUniversity.name, // Bar series for "YOU" university
          university: youUniversity.name
        });
      });
    }

    this._unifiedChartData = unifiedData;

    // console.log('✅ AI Citations: Unified chart data prepared:', {
    //   totalItems: this._unifiedChartData.length,
    //   youUniversity: youUniversity?.name,
    //   competitorCount: competitorUniversities.length
    // });
  }

  // Prepare combo chart data for ngx-charts-combo-chart
  private prepareComboChartData(youUniversity: any, competitorUniversities: any[]): void {
    // console.log('🎯 AI Citations: Preparing combo chart data for ngx-charts-combo-chart');
    // console.log('📊 AI Citations: You university:', youUniversity?.name);
    // console.log('📈 AI Citations: Competitor universities:', competitorUniversities.map(u => u.name));

    // Prepare bar chart data (results) - YOU university metrics as bars
    if (youUniversity) {
      this.results = [
        { name: 'Citation Frequency', value: youUniversity.citation_frequency || 0 },
        { name: 'Citation Share of Voice', value: youUniversity.citation_share_of_voice || 0 },
        { name: 'Authority Weight', value: youUniversity.authority_weight || 0 },
        { name: 'Relevance Score', value: youUniversity.relevance_score || 0 },
        { name: 'AI Readability', value: youUniversity.ai_readability || 0 },
        { name: 'External Mentions', value: youUniversity.external_mentions || 0 },
        { name: 'Diversity Index', value: youUniversity.diversity_index || 0 },
        { name: 'Persistence Score', value: youUniversity.persistence_score || 0 }
      ];
    } else {
      this.results = [];
    }

    // Prepare line chart data (lineChart) - competitor universities as lines
    if (competitorUniversities.length > 0) {
      this.lineChart = competitorUniversities.map(university => ({
        name: university.name,
        series: [
          { name: 'AI Citations Score', value: university.ai_visibility_score || 0 },
          { name: 'Content Quality', value: university.content_quality || 0 },
          { name: 'Digital Presence', value: university.digital_presence || 0 },
          { name: 'Innovation Index', value: university.innovation_index || 0 },
          { name: 'AI Adoption Rate', value: university.ai_adoption_rate || 0 }
        ]
      }));
    } else {
      this.lineChart = [];
    }

    // Set colors
    this.colors = this.comboBarScheme;

    // Set chart dimensions and transforms
    this.dims = {
      width: this.width,
      height: this.height,
      xAxisHeight: 50,
      yAxisWidth: 80,
      xAxisWidth: this.width - 80,
      yAxisHeight: this.height - 50
    };

    this.transform = `translate(${this.dims.yAxisWidth}, 0)`;

    // console.log('✅ AI Citations: Combo chart data prepared:', {
    //   barResults: this.results.length,
    //   lineSeries: this.lineChart.length,
    //   youUniversity: youUniversity?.name,
    //   competitorCount: competitorUniversities.length
    // });
  }

  // Get all available metrics from a university object dynamically
  private getAvailableMetrics(university: any): any[] {
    const metrics = [];

    // Check for standard citation metrics
    if (university.citation_frequency !== undefined) {
      metrics.push({ name: 'Citation Frequency', value: university.citation_frequency });
    }
    if (university.citation_share_of_voice !== undefined) {
      metrics.push({ name: 'Citation Share of Voice', value: university.citation_share_of_voice });
    }
    if (university.authority_weight !== undefined) {
      metrics.push({ name: 'Authority Weight', value: university.authority_weight });
    }
    if (university.relevance_score !== undefined) {
      metrics.push({ name: 'Relevance Score', value: university.relevance_score });
    }
    if (university.ai_readability !== undefined) {
      metrics.push({ name: 'AI Readability', value: university.ai_readability });
    }
    if (university.external_mentions !== undefined) {
      metrics.push({ name: 'External Mentions', value: university.external_mentions });
    }
    if (university.diversity_index !== undefined) {
      metrics.push({ name: 'Diversity Index', value: university.diversity_index });
    }
    if (university.persistence_score !== undefined) {
      metrics.push({ name: 'Persistence Score', value: university.persistence_score });
    }

    // Add any other numeric metrics
    Object.keys(university).forEach(key => {
      if (typeof university[key] === 'number' &&
          !['citation_frequency', 'citation_share_of_voice', 'authority_weight', 'relevance_score', 'ai_readability', 'external_mentions', 'diversity_index', 'persistence_score'].includes(key)) {
        const metricName = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        metrics.push({ name: metricName, value: university[key] });
      }
    });

    return metrics;
  }

  // Get metric value for a specific university and metric name - dynamic approach
  private getMetricValue(university: any, metricName: string): number {
    // Convert metric name back to property key
    const propertyKey = metricName.toLowerCase().replace(/\s+/g, '_');

    // Try direct property access first
    if (university[propertyKey] !== undefined) {
      return university[propertyKey];
    }

    // Try common variations
    const variations = [
      propertyKey,
      propertyKey.replace('_', ''),
      propertyKey.replace(/_/g, ''),
      metricName.toLowerCase().replace(/\s+/g, ''),
      metricName.toLowerCase().replace(/\s+/g, '_')
    ];

    for (const variation of variations) {
      if (university[variation] !== undefined) {
        return university[variation];
      }
    }

    // Fallback to 0 if not found
    console.warn(`AI Citations: Metric "${metricName}" not found for university ${university.name}`);
    return 0;
  }

  // Check if a university is the "you" university
  isYouUniversity(university: any): boolean {
    if (!university) return false;

    // Check if university name contains "(YOU)" or "you"
    if (university.name && university.name.toLowerCase().includes('(you)')) {
      return true;
    }

    // Check if university name contains "you"
    if (university.name && university.name.toLowerCase().includes('you')) {
      return true;
    }

    // Check for is_your_brand property
    if (university.is_your_brand === true) {
      return true;
    }

    return false;
  }

  // Find university with "you" in the name or marked as your brand
  private findYouUniversity(universities: any[]): any | null {
    // If there's only one university, treat it as the "you" university
    if (universities.length === 1) {
      // console.log('🎯 AI Citations: Single university detected, treating as "you" university:', universities[0].name);
      return universities[0];
    }

    // First, try to find university with "(YOU)" in the name
    let youUniversity = universities.find(university =>
      university.name && university.name.toLowerCase().includes('(you)')
    );

    // If not found, try to find university with "you" in the name
    if (!youUniversity) {
      youUniversity = universities.find(university =>
        university.name && university.name.toLowerCase().includes('you')
      );
    }

    // If still not found, check for is_your_brand property
    if (!youUniversity) {
      youUniversity = universities.find(university =>
        university.is_your_brand === true
      );
    }

    // If still not found, use the first university as fallback
    if (!youUniversity && universities.length > 0) {
      youUniversity = universities[0];
    }

    return youUniversity || null;
  }

  // Find competitor universities (those without "you" in name and not marked as your brand)
  private findCompetitorUniversities(universities: any[]): any[] {
    const youUniversity = this.findYouUniversity(universities);
    if (!youUniversity) return universities;

    return universities.filter(university =>
      university.name !== youUniversity.name
    );
  }

  // Get processed data for charts with caching
  getProcessedBarData() {
    if (this._cachedBarData.length === 0) {
      this._cachedBarData = this.barChartData;
    }
    return this._cachedBarData;
  }

  getProcessedLineData() {
    if (this._cachedLineData.length === 0) {
      this._cachedLineData = this.lineChartData;
    }
    return this._cachedLineData;
  }

  // Get the name of the first university (bar data) with caching
  getFirstUniversityName(): string {
    if (this._cachedFirstUniversityName === '' && this.universities) {
      const youUniversity = this.findYouUniversity(this.universities);
      this._cachedFirstUniversityName = youUniversity ? youUniversity.name : '';
    }
    return this._cachedFirstUniversityName;
  }

  // Get line color for competitor universities
  getLineColor(universityName: string): string {
    const colors = ['#DB4437', '#F4B400', '#0F9D58', '#AB47BC'];
    const competitorUniversities = this.findCompetitorUniversities(this.aiCitationsResponse?.universities || []);
    const index = competitorUniversities.findIndex(uni => uni.name === universityName);
    return colors[index % colors.length];
  }

  // Get custom colors for single university bar chart
  getCustomColors(): any[] {
    const colors = [
      { name: 'Citation Frequency', value: '#1976d2' },
      { name: 'Citation Share of Voice', value: '#ff9800' },
      { name: 'Authority Weight', value: '#4caf50' },
      { name: 'Relevance Score', value: '#f44336' },
      { name: 'AI Readability', value: '#9c27b0' },
      { name: 'External Mentions', value: '#00bcd4' },
      { name: 'Diversity Index', value: '#795548' },
      { name: 'Persistence Score', value: '#607d8b' }
    ];

    // Map the colors to the actual data
    return this.barChartData.map((item, index) => ({
      name: item.name,
      value: colors[index % colors.length]?.value || '#1976d2'
    }));
  }

  private clearCache(): void {
    // console.log('🧹 AI Citations: Clearing all cached data');
    this._cachedBarData = [];
    this._cachedLineData = [];
    this._cachedFirstUniversityName = '';

    // Also clear the main data arrays
    this.barChartData = [];
    this.lineChartData = [];
    // console.log('🧹 AI Citations: All data arrays cleared');
  }

  createCitationBadge(citation: any): any {
    return {
      source_badge: `📊 ${citation.type || 'source'}`,
      live_badge: '🟢 Live',
      total_sources: 1,
      llm_sources: citation.type === 'llm' ? 1 : 0,
      bing_sources: citation.type === 'bing' ? 1 : 0,
      average_confidence: citation.confidence || 0.8
    };
  }

  transformCitationsToEvidenceSources(citations: any[]): any[] {
    return citations.map(citation => ({
      category: citation.category || 'ai-citations',
      source: citation.source,
      url: citation.url,
      confidence: citation.confidence || 0.7,
      snippet: citation.snippet || '',
      type: citation.type || 'web'
    }));
  }

  // Prepare strengths and improvement data from universities
  getStrengthsImprovementData(): StrengthsImprovementData | null {
    if (!this.universities || this.universities.length === 0) {
      return null;
    }

    const brands: BrandAnalysis[] = this.universities.map((university, index) => {
      // Check if university has explicit strengths/improvements from API
      const apiStrengths = university.strengths || [];
      const apiImprovements = university.improvements || university.areas_for_improvement || [];

      // If API provides data, use it; otherwise use generated data based on metrics
      const strengths = apiStrengths.length > 0 ? apiStrengths : this.generateStrengths(university, index);
      const improvements = apiImprovements.length > 0 ? apiImprovements : this.generateImprovements(university, index);

      return {
        name: university.name,
        strengths: strengths,
        improvements: improvements,
        isYouBrand: this.isYouUniversity(university)
      };
    });

    return {
      brands: brands,
      title: 'Strengths and Areas for Improvement',
      subtitle: 'Comprehensive analysis of university performance'
    };
  }

  // Generate strengths based on university metrics
  private generateStrengths(university: any, index: number): string[] {
    const strengths: string[] = [];
    
    // Base strengths based on high-performing metrics
    if (university.citation_frequency && university.citation_frequency > 75) {
      strengths.push('High citation frequency in AI models');
    }
    if (university.authority_weight && university.authority_weight > 80) {
      strengths.push('Strong domain authority');
    }
    if (university.relevance_score && university.relevance_score > 85) {
      strengths.push('Excellent content relevance');
    }
    if (university.ai_readability && university.ai_readability > 80) {
      strengths.push('AI-friendly content structure');
    }

    // Add generic strengths based on index for variety
    if (index === 0) {
      strengths.push('Strong community links', 'Diverse curriculum');
    } else if (index === 1) {
      strengths.push('Industry connections', 'High student satisfaction');
    } else {
      strengths.push('Research excellence', 'Global partnerships');
    }

    // Ensure at least some strengths
    if (strengths.length === 0) {
      strengths.push('Innovative programs', 'Strong alumni network');
    }

    return strengths;
  }

  // Generate improvements based on university metrics
  private generateImprovements(university: any, index: number): string[] {
    const improvements: string[] = [];
    
    // Base improvements on low-performing metrics
    if (university.citation_frequency && university.citation_frequency < 50) {
      improvements.push('Increase citation frequency');
    }
    if (university.external_mentions && university.external_mentions < 60) {
      improvements.push('Expand external mention presence');
    }
    if (university.diversity_index && university.diversity_index < 55) {
      improvements.push('Improve content diversity');
    }
    if (university.persistence_score && university.persistence_score < 65) {
      improvements.push('Enhance long-term visibility');
    }

    // Add generic improvements based on index for variety
    if (index === 0) {
      improvements.push('Lower research output', 'Limited international partnerships');
    } else if (index === 1) {
      improvements.push('High fees for international students', 'Competitive entry requirements');
    } else {
      improvements.push('Limited local industry ties', 'Smaller class sizes');
    }

    // Ensure at least some improvements
    if (improvements.length === 0) {
      improvements.push('Technology infrastructure', 'Student support services');
    }

    return improvements;
  }
}
